import React, { useState, useEffect } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import DynamicSelect from "../../dynamic_select/DynamicSelect";
import { useFormContext } from "react-hook-form";
import './billing.scss';

const Billing = ({      
    selectedCompany, 
    fetchCompany,
    currencyOptions,
    billingModelOptions,
}) =>{
    const {
        register,
        watch,
        setValue,
        reset,
        formState: { isDirty },
    } = useFormContext();

    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        register('currency');
        register('billing_model');
        register('company_billings');
    }, [register]);
    
    const [currency, billing_model, company_billings] = watch(['currency', 'billing_model', 'company_billings']);
    const fixedCarTypes = ['premium_sedan', 'van', 'minibus', 'hatchback'];

    // Row headings
    const carTypeNames = {
        premium_sedan: "Premium Sedan",
        van: "Van",
        minibus: "Minibus",
        hatchback: "Hatchback"
    };
    
    // Column headings
    const columns = [
        { label: "Car Type", key: "car_type"},
        { label: "0 - 19km", key: "km0_km19"},
        { label: "20 - 29km", key: "km20_km29" },
        { label: "30 - 39km", key: "km30_km39" },
        { label: "40 - 49km", key: "km40_km49" },
        { label: "50 - 59km", key: "km50_km59" },
        { label: "60 - 69km", key: "km60_km69" },
        { label: "70 - 79km", key: "km70_km79" },
        { label: "80 - 89km", key: "km80_km89" },
        { label: "90km +", key: "km90" },
        { label: "Cancelled", key: "cancelled" },
        { label: "App Fee", key: "app_fee" },
        { label: "Vat %", key: "vat_percentage" },
    ];

    // Save changes
    const updateCompany = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance(`/companies/${selectedCompany.id}.json`, {
                method: 'PUT',
                data: {
                    company: {
                        billing_model: billing_model,
                        currency: currency,
                    } 
                }
            });

            await fetchCompany();

        } catch (error) {
            console.error("Company can't be updated.", error)
        } finally {
            setIsLoading(false);
        }
    };

    // Revert unsaved changes
    const handleRevertChanges = async () => {
        reset();
    };

    // Vehicle pricing changes
    const handleInputChange = (billingIndex, key, event) => {
        const companyBillings = [...JSON.parse(company_billings)];
        companyBillings[billingIndex] = {
            ...companyBillings[billingIndex],
            [key]: event,
        };
        setValue('company_billings', JSON.stringify(companyBillings), {
            shouldDirty: true
        });
    };

    // Save button in vehicle pricing table row
    const handleSubmit = async (billingIndex) => {
        // Get the billing values for specific car type
        const company_billing = JSON.parse(company_billings)[billingIndex] || {};

        // Save input value or set 0
        const updatedCompanyBilling = {
            cancelled: parseFloat(company_billing.cancelled) || 0,
            km0_km19: parseFloat(company_billing.km0_km19) || 0,
            km20_km29: parseFloat(company_billing.km20_km29) || 0,
            km30_km39: parseFloat(company_billing.km30_km39) || 0,
            km40_km49: parseFloat(company_billing.km40_km49) || 0,
            km50_km59: parseFloat(company_billing.km50_km59) || 0,
            km60_km69: parseFloat(company_billing.km60_km69) || 0,
            km70_km79: parseFloat(company_billing.km70_km79) || 0,
            km80_km89: parseFloat(company_billing.km80_km89) || 0,
            km90: parseFloat(company_billing.km90) || 0,
            currency: currency || "za",
            app_fee: parseFloat(company_billing.app_fee) || 0,
            vat_percentage: parseFloat(company_billing.vat_percentage) || 0
        };

        setIsLoading(true);
        try {
            const response = await axiosInstance({
                url: `/companies/${selectedCompany.id}/company_billings/${company_billing.id}.json`,
                method: 'PUT',
                data: {
                    company_billing: updatedCompanyBilling
                },
                headers: { Accept: 'application/json' }
            });

            await fetchCompany();
        } catch (error) {
            console.error("Failed to update company billing: ", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Clear button in table row
    const handleClearChanges = (index) => {
        reset();
    };

    return (
        <div className="w-full p-4 flex flex-col">

            {/* Billing Options section */}
            <div className="billing_options flex flex-wrap px-3 md:px-0">

                <div className="w-full md:w-1/4 flex flex-col pr-5 mb-4 md:mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                    <div className="w-full pr-5">
                        <h3 className="text-lg font-medium text-gray-700">Billing Options</h3>
                        <p className="text-sm text-gray-500">Details about billing</p>
                    </div>
                </div>

                <div className="w-full md:w-3/4 flex flex-col">
                    <div className="w-full flex flex-wrap">
                        <div className="w-full md:w-1/2 flex flex-col pr-1">
                            <label className="label font-medium text-gray-900">Billing Model</label>
                            <DynamicSelect
                                props={{
                                    onChange: (selected) => {
                                        setValue('billing_model', selected?.target.value?.value || '', {
                                            shouldDirty: true
                                        });
                                    },
                                    options: billingModelOptions,
                                    placeholder: "Select Billing Model",
                                    value: billing_model,
                                    closeMenuOnSelect: true
                                }}
                            />
                        </div>
                        
                        <div className="w-full md:w-1/2 flex flex-col pl-1">
                            <label className="label font-medium text-gray-900">Company Currency</label>
                            <DynamicSelect
                                props={{
                                    onChange: (selected) => {
                                        setValue('currency', selected?.target.value?.value || '', {
                                            shouldDirty: true
                                        });
                                    },
                                    options: currencyOptions,
                                    placeholder: "Select Currency",
                                    value: currency,
                                    closeMenuOnSelect: true
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
            
            <hr className="border border-gray-200 my-5" />

            {/* Vehicle Pricing section */}
            <div className="vehicle_pricing flex flex-wrap px-3 md:px-0">

                <div className="w-full md:w-1/4 flex flex-col pr-5 mb-4 md:mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                    <div className="w-full pr-5">
                        <h3 className="text-lg font-medium text-gray-700">Vehicle Pricing</h3>
                        <p className="text-sm text-gray-500 pb-3">Pricing per vehicle type</p>
                    </div>
                </div>

                <div className="vehicle_pricing overflow-visible rounded overflow-y-auto dark-gray-scrollbar w-full">
                    <table className="w-full text-sm table-auto">
                        <thead>
                            <tr>
                                {columns.map((col) => (
                                    <th key={col.key} className="text-left p-2">{col.label}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {fixedCarTypes.map((carType) => {
                                const companyBillings = company_billings ? JSON.parse(company_billings) : [];
                                const billingIndex = companyBillings.findIndex((billing) => billing.car_type == carType);
                                const billing = companyBillings[billingIndex];
                                return (
                                    <tr key={carType}>
                                        <td className="text-sm font-medium text-gray-600 p-2" style={{ whiteSpace: 'nowrap' }}>
                                            <div>{carTypeNames[carType]}</div>
                                        </td>
                                        {columns.slice(1).map((col) => (
                                            <td key={col.key} className="p-2">
                                                <input
                                                    type="number"
                                                    value={billing[col?.key] || ""}
                                                    className="input billing-input"
                                                    style={{ paddingBottom: '6px', paddingTop: '6px', paddingLeft: '5px', width: '90px' }}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        if (/^\d*\.?\d*$/.test(value)) {
                                                            handleInputChange(billingIndex, col.key, value);
                                                        }
                                                    }}                                    
                                                />
                                            </td>
                                        ))}
                                        
                                        <td className="relative w-full md:w-1/6 flex space-x-2">
                                            {/* Submit vehicle pricing values */}
                                            <button
                                                className={`button text-white rounded text-sm flex items-center justify-center relative cursor-pointer`}
                                                style={{ width: "30px", height: "30px" }}
                                                // Pass row identifier 
                                                onClick={()=> handleSubmit(billingIndex)}                                       
                                            >
                                                <i className="fas fa-save" />
                                            </button>

                                            {/* Undo vehicle pricing changes */}
                                            <button
                                                className="py-2 px-2 bg-gray-400 text-white rounded font-bold text-sm flex items-center justify-center relative cursor-pointer" 
                                                onClick={()=> handleClearChanges(billingIndex)}                                           
                                            >
                                                <i className="fas fa-undo" />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Save and revert buttons */}
            <div className="relative pt-20">
                <div className="save_revert_buttons absolute bottom-0 right-0 flex space-x-3">
                    <button
                        className={`button p-2 text-white rounded text-sm flex align-center justify-center relative cursor-pointer ${
                            isLoading ? "bg-gray-400" : "button"
                        }`}
                        style={{ width: "156px", userSelect: "none" }}
                        onClick={()=> updateCompany()}
                        disabled={isLoading}
                    >
                        {isLoading ? "Saving..." : "Save"}
                    </button>
                    <button
                        className={`p-2 text-white rounded text-sm flex items-center justify-center relative cursor-pointer bg-gray-500 hover:bg-gray-600`}
                        style={{ width: "156px", userSelect: "none" }}
                        onClick={() => handleRevertChanges()}
                        disabled={isDirty == false}
                    >
                        Revert
                    </button>
                </div>
            </div>
        </div>
    );
}

export default Billing;